import { AuthManager, User } from './authManager';
import { UserTrialManager } from './userTrialManager';

export interface TestUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  password: string;
  zipcode: string;
  city: string;
  state: string;
  country: string;
  trialStatus?: 'none' | 'active' | 'expired';
  trialDaysLeft?: number;
}

export const TEST_USERS: TestUser[] = [
  {
    id: 'user1',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '(555) 123-4567',
    address: '123 Main Street, Arlington, TX 76010',
    password: 'password123',
    zipcode: '76010',
    city: 'Arlington',
    state: 'TX',
    country: 'USA',
    trialStatus: 'active',
    trialDaysLeft: 15,
  },
  {
    id: 'user2',
    name: 'Michael Chen',
    email: 'michael.chen@email.com',
    phone: '(555) 234-5678',
    address: '456 Oak Avenue, Arlington, TX 76011',
    password: 'testpass456',
    zipcode: '76011',
    city: 'Arlington',
    state: 'TX',
    country: 'USA',
    trialStatus: 'none',
  },
  {
    id: 'user3',
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@email.com',
    phone: '(555) 345-6789',
    address: '789 Pine Street, Arlington, TX 76012',
    password: 'userpass789',
    zipcode: '76012',
    city: 'Arlington',
    state: 'TX',
    country: 'USA',
    trialStatus: 'expired',
    trialDaysLeft: 0,
  },
  {
    id: 'user4',
    name: 'David Thompson',
    email: 'david.thompson@email.com',
    phone: '(555) 456-7890',
    address: '321 Elm Drive, Arlington, TX 76013',
    password: 'demo123456',
    zipcode: '76013',
    city: 'Arlington',
    state: 'TX',
    country: 'USA',
    trialStatus: 'active',
    trialDaysLeft: 7,
  },
  {
    id: 'user5',
    name: 'Lisa Anderson',
    email: 'lisa.anderson@email.com',
    phone: '(555) 567-8901',
    address: '654 Maple Lane, Arlington, TX 76014',
    password: 'testuser999',
    zipcode: '76014',
    city: 'Arlington',
    state: 'TX',
    country: 'USA',
    trialStatus: 'none',
  },
  {
    id: 'customer_1',
    name: 'John Doe',
    email: 'customer1@test.com',
    phone: '(111) 222-3333',
    address: '123 Test Street, Test City, TX 75001',
    password: 'test123',
    zipcode: '75001',
    city: 'Test City',
    state: 'TX',
    country: 'USA',
    trialStatus: 'none',
  },
  {
    id: 'customer_2',
    name: 'Jane Smith',
    email: 'customer2@test.com',
    phone: '(555) 987-6543',
    address: '456 Test Avenue, Test City, TX 75002',
    password: 'test123',
    zipcode: '75002',
    city: 'Test City',
    state: 'TX',
    country: 'USA',
    trialStatus: 'none',
  },
];

export class TestUserManager {
  static async initializeTestUsers(): Promise<void> {
    try {
      console.log('Initializing test users...');
      
      for (const testUser of TEST_USERS) {
        // Create user object
        const user: User = {
          id: testUser.id,
          name: testUser.name,
          username: testUser.email, // Use email as username
          password: testUser.password,
          email: testUser.email,
          phone: testUser.phone,
          address: testUser.address,
          userType: 'customer',
          registrationDate: new Date().toISOString(),
        };

        // Save user to storage
        await AuthManager.saveUser(user);
        
        // Set up trial status if applicable
        if (testUser.trialStatus === 'active') {
          // Create active trial
          const trialData = await UserTrialManager.startTrial('premium');
          console.log(`Created active trial for ${testUser.name}: ${trialData.daysLeft} days left`);
        } else if (testUser.trialStatus === 'expired') {
          // Create expired trial
          await UserTrialManager.simulateTrialExpiration(1);
          console.log(`Created expired trial for ${testUser.name}`);
        }

        console.log(`✓ Created test user: ${testUser.name} (${testUser.email})`);
      }

      console.log('✅ All test users initialized successfully!');
    } catch (error) {
      console.error('Error initializing test users:', error);
    }
  }

  static async clearTestUsers(): Promise<void> {
    try {
      console.log('Clearing test users...');
      
      for (const testUser of TEST_USERS) {
        await AuthManager.logoutUser();
        await UserTrialManager.clearTrialData();
      }
      
      console.log('✅ Test users cleared successfully!');
    } catch (error) {
      console.error('Error clearing test users:', error);
    }
  }

  static getTestUserCredentials(): { email: string; password: string; name: string }[] {
    return TEST_USERS.map(user => ({
      email: user.email,
      password: user.password,
      name: user.name,
    }));
  }

  static getTestUserByEmail(email: string): TestUser | undefined {
    return TEST_USERS.find(user => user.email === email);
  }

  static async loginTestUser(email: string, password: string): Promise<boolean> {
    console.log('🔍 TestUserManager.loginTestUser called with:', { email, password });
    const testUser = this.getTestUserByEmail(email);
    console.log('🔍 Found test user:', testUser ? testUser.name : 'null');
    
    if (!testUser || testUser.password !== password) {
      console.log('🔍 Login failed - user not found or password mismatch');
      return false;
    }

    try {
      // Create user object for login
      const user: User = {
        id: testUser.id,
        name: testUser.name,
        username: testUser.email, // Use email as username
        password: testUser.password,
        email: testUser.email,
        phone: testUser.phone,
        address: testUser.address,
        userType: 'customer',
        registrationDate: new Date().toISOString(),
      };

      // Save user (this automatically marks as logged in)
      await AuthManager.saveUser(user);
      
      console.log(`✅ Test user logged in: ${testUser.name}`);
      return true;
    } catch (error) {
      console.error('Error logging in test user:', error);
      return false;
    }
  }
}
